import { Inject, Injectable, OnDestroy, PLATFORM_ID } from '@angular/core';
import { BehaviorSubject, Observable, Subject, catchError, distinctUntilChanged, filter, map, of, switchMap, take, takeUntil, tap, throwError } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { BackendConstants } from '../../../shared/constants/backend.constants';
import { UserProfile as User} from '../../_models/user.model';
import { AuthService } from '../api/auth.service';
import { isPlatformBrowser } from '@angular/common';

@Injectable({ providedIn: 'root' })
export class UserProfileService implements OnDestroy {
  private apiURL = BackendConstants.BACKEND_URL + "/";
  private profileSubject = new BehaviorSubject<User | null>(null);
  private destroy$ = new Subject<void>();
  private isInitialized = false;

  constructor(
    private http: HttpClient, 
    private authService: AuthService,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {
    this.setupAuthSubscription();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private setupAuthSubscription(): void {
    this.authService.isLoggedIn$
      .pipe(
        distinctUntilChanged(),
        takeUntil(this.destroy$)
      )
      .subscribe((isLoggedIn) => {
        if (isLoggedIn) {
          if (!this.authService.isAdmin()) {
            this.loadProfile().subscribe();
          } else {
            this.isInitialized = true;
          }
        } else {
          this.clearProfile();
        }
      });
  }

  private loadProfile(): Observable<User> {
    if (!this.authService.isLoggedIn()) {
      this.clearProfile();
      return throwError(() => new Error('Not authenticated'));
    }

    if (this.authService.isAdmin()) {
      this.isInitialized = true;
      return of(null as unknown as User);
    }

    const url = this.apiURL + this.getApiProfileUrl();
    return this.http.get<User>(url).pipe(
      tap((profile) => {
        this.profileSubject.next(profile);
        this.isInitialized = true;
        if (profile.profileImage) {
          this.authService.setUserProfileImage(profile.profileImage);
        }
      }),
      catchError((error) => {
        console.error('Failed to load user profile', error);
        this.clearProfile();
        return throwError(() => error);
      })
    );
  }

  get profile$(): Observable<User | null> {
    if (!this.isInitialized && this.authService.isLoggedIn()) {
      if (this.authService.isAdmin()) {
        this.isInitialized = true;
        return of(null);
      }
      return this.loadProfile().pipe(
        switchMap(() => this.profileSubject.asObservable())
      );
    }
    return this.profileSubject.asObservable();
  }

  get currentProfile(): User | null {
    return this.profileSubject.value;
  }

  updateProfile(updateData: Partial<User>, file: File | null): Observable<User> {
    const currentProfile = this.currentProfile;
    if (!currentProfile) {
      return throwError(() => new Error('No profile loaded'));
    }
    const formData = new FormData();
    const companyBlob = new Blob([JSON.stringify(updateData)], { type: 'application/json' });
    formData.append('profile', companyBlob);
    if(file){
      formData.append('image', file);
    }

    const url = this.getApiProfileUrl();
    return this.http.put<User>(this.apiURL + 'profile', formData).pipe(
      tap((updatedProfile) => {
        this.profileSubject.next(updatedProfile);
        if (updatedProfile.profileImage !== currentProfile.profileImage) {
          this.authService.setUserProfileImage(updatedProfile.profileImage);
        }
      })
    );
  }

  refreshProfile(): Observable<User> {
    return this.loadProfile();
  }

  clearProfile(): void {
    this.profileSubject.next(null);
    this.isInitialized = false;
  }

  // Tab synchronization
  syncAcrossTabs(): void {
    if (isPlatformBrowser(this.platformId)) {
      window.addEventListener('storage', (event) => {
        if (event.key === 'profile_update') {
          if (this.authService.isLoggedIn()) {
            this.refreshProfile().subscribe();
          }
        }
      });
    }
  }

  notifyOtherTabs(): void {
    if (isPlatformBrowser(this.platformId)) {
      window.localStorage.setItem('profile_update', Date.now().toString());
      // Clean up after notification
      setTimeout(() => {
        window.localStorage.removeItem('profile_update');
      }, 100);
    }
  }

 private getApiProfileUrl(): string {
    return this.authService.isUser() ? 'customer/profile' 
           : (this.authService.isAgent() ? 'agent/profile' : '/profile');
  }
}
